import { useEffect, useRef, useState } from "react";
import {
  detachGuildsStream,
  exchangeCode,
  fetchGuilds,
  fetchGuildsStream,
  getToken,
  setSession,
} from "../auth/session";
import { isApiError, loginUrl } from "../auth/api";
import { guildIconCdnUrl } from "@cookies/shared/cdn";
import type { BakeryGuild, GuildStreamHandlers, SessionUser } from "../auth/session";

let serversSeq = 0;

const CONSUMED_KEY = "cb_oauth_consumed_code";

const persistedKeys = new Set<string>();
const notifiedKeys = new Set<string>();

function persistOnce(key: string, token: string, user: SessionUser): void {
  if (persistedKeys.has(key)) return;
  persistedKeys.add(key);
  setSession(token, user);
}

function notifyOnce(key: string, done: () => void): boolean {
  if (notifiedKeys.has(key)) return true;
  notifiedKeys.add(key);
  done();
  return true;
}

function readConsumed(): string | null {
  try {
    return sessionStorage.getItem(CONSUMED_KEY);
  } catch {
    return null;
  }
}

function markConsumed(code: string): void {
  try {
    sessionStorage.setItem(CONSUMED_KEY, code);
  } catch {
    return;
  }
}

function isAbort(e: unknown): boolean {
  return e instanceof DOMException && e.name === "AbortError";
}

function consumedMessage(msg: string): boolean {
  const m = msg.toLowerCase();
  return (
    m.includes("already") ||
    m.includes("consumed") ||
    m.includes("invalid_code") ||
    m.includes("invalid code") ||
    m.includes("expired")
  );
}

function isLockedGuild(g: BakeryGuild): boolean {
  // Policy (HTTP REST only, no gateway): locked = bot not present.
  // joinable = member + bot present. Embed status is informational only and
  // must never lock or show "Missing access".
  if (g.joinable === true) return false;
  if (g.botPresent === true) return false;
  if (g.botPresent === false) return true;
  if (g.joinable === false) return true;
  const r = (g.botReason ?? "").trim().toLowerCase();
  if (r === "bot_not_in_guild" || r === "unknown_guild" || r === "app_not_installed") return true;
  if (r === "" || r === "checking" || r === "rate_limited" || r === "fetch_failed") return false;
  if (r.startsWith("discord_status_")) return false;
  return false;
}

function guildIconUrl(g: BakeryGuild): string | null {
  return guildIconCdnUrl(g.id, g.icon ?? null, 64);
}

function GuildIcon({ url, name, locked }: { url: string | null; name: string; locked: boolean }) {
  const [failed, setFailed] = useState(false);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    if (!url || failed || loaded) return;
    const t = window.setTimeout(() => setFailed(true), 1000);
    return () => window.clearTimeout(t);
  }, [url, failed, loaded]);
  if (!url || failed) return <>{name.slice(0, 1) || "🏠"}</>;
  return (
    <img
      src={url}
      alt={name}
      width={30}
      height={30}
      crossOrigin="anonymous"
      style={{ borderRadius: 8, filter: locked ? "grayscale(1)" : undefined }}
      onLoad={() => setLoaded(true)}
      onError={() => setFailed(true)}
    />
  );
}

function friendlyReason(raw: string | null | undefined): string {
  if (raw === null || raw === undefined) return "Unavailable";
  const code = raw.trim().toLowerCase();
  if (code === "") return "Unavailable";
  if (code === "checking") return "Checking Discord status…";
  if (code === "bot_not_in_guild" || code === "app_not_installed" || code === "requires_bot") return "Bot not installed";
  if (code === "missing_access" || code === "bot_missing_permission" || code === "forbidden") return "Missing access — reinstall the app";
  if (code === "unknown_guild" || code === "unknown_bakery" || code === "bad_guild_id" || code === "bad_payload") return "Bakery not found";
  if (code === "preview_not_enabled") return "Checking Discord status…";
  if (code === "surface_not_supported" || code === "unsupported" || code.startsWith("unsupported_reason_")) return "Not supported in this server";
  if (code.startsWith("discord_status_")) return "Discord check failed — try again";
  if (code === "rate_limited" || code === "fetch_failed") return "Checking Discord status…";
  if (code === "bot_token_invalid" || code === "no_bot_token" || code === "no_client_id" || code === "no_bot_id") return "Unavailable — try again";
  return "Unavailable";
}

function isCheckingGuild(g: BakeryGuild): boolean {
  const b = (g.botReason ?? "").trim().toLowerCase()
  const e = (g.embedReason ?? "").trim().toLowerCase()
  return b === "checking" || e === "checking"
}

function guildReason(g: BakeryGuild): string {
  if (g.joinable === true) return "";
  if (g.botPresent === true) return "";
  if (g.botPresent === false) {
    const r = (g.botReason ?? "").trim().toLowerCase();
    if (r === "bot_not_in_guild" || r === "unknown_guild" || r === "app_not_installed") return friendlyReason(g.botReason ?? "bot_not_in_guild");
    if (r === "" || r === "checking" || r === "rate_limited" || r === "fetch_failed") return friendlyReason(g.botReason ?? "checking");
    if (r.startsWith("discord_status_")) return friendlyReason(g.botReason ?? "unavailable");
    return friendlyReason(g.botReason ?? "unavailable");
  }
  if (g.joinable === false) return friendlyReason(g.botReason ?? "unavailable");
  return "";
}

function normalizeGuildId(raw: string): string | null {
  const t = raw.trim();
  if (/^[0-9]{17,20}$/.test(t)) return t;
  return null;
}

function friendlyLoadError(msg: string): string {
  const low = msg.trim().toLowerCase();
  if (low === "") return "Could not load bakeries.";
  if (low.includes("discord_status_")) return "Discord check failed — try again.";
  if (low === "checking") return "Checking Discord status…";
  if (low === "bot_not_in_guild" || low === "app_not_installed" || low === "requires_bot") return "Bot not installed.";
  if (low === "missing_access" || low === "bot_missing_permission" || low === "forbidden") return "Missing access — reinstall the app.";
  if (low === "unknown_guild" || low === "unknown_bakery" || low === "bad_guild_id") return "Bakery not found.";
  return msg;
}

function guildSubline(g: BakeryGuild): string {
  const parts: string[] = [];
  if (typeof g.memberCount === "number") parts.push(g.memberCount + (g.memberCount === 1 ? " baker" : " bakers"));
  if (typeof g.bakeryLevel === "number") parts.push("Lv. " + g.bakeryLevel);
  if (parts.length > 0) return parts.join(" · ");
  return "shared bakery";
}

export function LandingPage() {
  return (
    <div className="cb-page">
      <div className="cb-panel hl center">
        <div style={{ fontSize: 64 }} aria-hidden="true">🍪</div>
        <h1 className="cb-h">Cookie Bakery</h1>
        <p className="small">
          Everyone in the Discord server is a baker. Click the giant cookie
          together, earn shared cookies, and grow your bakery.
        </p>
        <a className="cb-buy" href={loginUrl()} style={{ textDecoration: "none", display: "inline-block" }}>
          Continue with Discord
        </a>
        <p className="small dim">Sign in to pick your bakery and start baking.</p>
      </div>
    </div>
  );
}

export function CallbackPage({ onDone }: { onDone: () => void }) {
  const [status, setStatus] = useState("Finishing Discord sign-in…");
  const doneRef = useRef(onDone);
  useEffect(() => {
    doneRef.current = onDone;
  }, [onDone]);
  useEffect(() => {
    let mounted = true;
    const finish = (key: string) => {
      if (!mounted) return;
      if (notifiedKeys.has(key)) return;
      notifyOnce(key, doneRef.current);
    };
    const fail = (e: unknown, key: string | null) => {
      if (!mounted) return;
      if (isAbort(e)) return;
      if (e instanceof Error && consumedMessage(e.message)) {
        const existing = getToken();
        if (existing) {
          finish(key ?? "callback");
          return;
        }
        setStatus("This sign-in link was already used. Please sign in again.");
        return;
      }
      setStatus(e instanceof Error ? e.message : "Sign-in failed.");
    };
    const run = async () => {
      let key: string | null = null;
      try {
        const q = new URLSearchParams(window.location.search);
        if (q.get("error")) throw new Error("Discord sign-in was cancelled.");
        const direct = q.get("token");
        if (direct) {
          key = "token:" + direct;
          const userParam = q.get("user");
          if (!userParam) throw new Error("Sign-in failed.");
          let user: SessionUser;
          try {
            const parsed = JSON.parse(decodeURIComponent(userParam)) as Partial<SessionUser>;
            if (typeof parsed.id !== "string" || typeof parsed.username !== "string" || !parsed.id || !parsed.username) {
              throw new Error("Sign-in failed.");
            }
            user = { id: parsed.id, username: parsed.username, avatar: parsed.avatar ?? null };
          } catch {
            throw new Error("Sign-in failed.");
          }
          persistOnce(key, direct, user);
          finish(key);
          return;
        }
        const code = q.get("code");
        if (!code) throw new Error("Missing OAuth code.");
        key = "code:" + code;
        if (readConsumed() === code || notifiedKeys.has(key)) {
          const existing = getToken();
          if (existing) {
            finish(key);
            return;
          }
          setStatus("This sign-in link was already used. Please sign in again.");
          return;
        }
        const session = await exchangeCode(code);
        markConsumed(code);
        persistOnce(key, session.token, session.user);
        finish(key);
      } catch (e) {
        fail(e, key);
      }
    };
    void run();
    return () => {
      mounted = false;
    };
  }, []);
  return (
    <div className="cb-page">
      <div className="cb-panel hl center">
        <div style={{ fontSize: 48 }} aria-hidden="true">🍪</div>
        <h1 className="cb-h">Signing you in</h1>
        <p className="small" role="status" aria-live="polite">{status}</p>
      </div>
    </div>
  );
}

export function ServersPage({
  token,
  user,
  onJoin,
}: {
  token: string;
  user: SessionUser | null;
  onJoin: (guildId: string) => void;
}) {
  const [guilds, setGuilds] = useState<BakeryGuild[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<number | null>(null);
  const [unauthorized, setUnauthorized] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [joinErrors, setJoinErrors] = useState<Record<string, string>>({});
  const [refreshing, setRefreshing] = useState(false);
  function dedupeGuilds(list: BakeryGuild[]): BakeryGuild[] {
    const seen = new Set<string>();
    const out: BakeryGuild[] = [];
    for (const g of list) {
      if (!g || typeof g.id !== "string" || g.id === "") continue;
      if (seen.has(g.id)) continue;
      seen.add(g.id);
      out.push(g);
    }
    return out;
  }
  useEffect(() => {
    let mounted = true;
    const seq = ++serversSeq;
    setGuilds(null);
    setError(null);
    setErrorCode(null);
    setUnauthorized(false);
    let gotGuilds = false;
    const apply = (fn: () => void): void => {
      if (!mounted || seq !== serversSeq) return;
      fn();
    };
    const handleGuilds = (list: BakeryGuild[]): void => {
      gotGuilds = true;
      apply(() => setGuilds(dedupeGuilds(list)));
    };
    const handlePresence = (guild: BakeryGuild): void => {
      if (!guild || typeof guild.id !== "string" || guild.id === "") return;
      apply(() =>
        setGuilds((prev) => {
          if (!prev) return [guild];
          const idx = prev.findIndex((x) => x.id === guild.id);
          if (idx === -1) return [...prev, guild];
          const next = [...prev];
          const cur = next[idx];
          if (!cur) return prev;
          next[idx] = { ...cur, ...guild };
          return next;
        })
      );
    };
    const handlers: GuildStreamHandlers = {
      onGuilds: handleGuilds,
      onPresence: handlePresence,
      onDone: () => {},
    };
    const run = async (): Promise<void> => {
      try {
        await fetchGuildsStream(token, handlers);
        if (!mounted || seq !== serversSeq) return;
        if (!gotGuilds) {
          const g = await fetchGuilds(token);
          if (!mounted || seq !== serversSeq) return;
          apply(() => {
            setGuilds(g);
            setError(null);
            setErrorCode(null);
            setUnauthorized(false);
          });
        }
      } catch (e) {
        if (!mounted || seq !== serversSeq) return;
        if (e instanceof DOMException && e.name === "AbortError") return;
        if (isApiError(e) && e.status === 401) {
          apply(() => {
            setUnauthorized(true);
            setError(null);
            setErrorCode(null);
            setGuilds([]);
          });
          return;
        }
        try {
          const g = await fetchGuilds(token);
          if (!mounted || seq !== serversSeq) return;
          apply(() => {
            setGuilds(g);
            setError(null);
            setErrorCode(null);
            setUnauthorized(false);
          });
          return;
        } catch (fe) {
          if (!mounted || seq !== serversSeq) return;
          if (fe instanceof DOMException && fe.name === "AbortError") return;
          if (isApiError(fe) && fe.status === 401) {
            apply(() => {
              setUnauthorized(true);
              setError(null);
              setErrorCode(null);
              setGuilds([]);
            });
            return;
          }
          apply(() => {
            if (isApiError(fe)) {
              setError(fe.message);
              setErrorCode(fe.status);
            } else {
              setError(fe instanceof Error ? fe.message : "Could not load bakeries.");
              setErrorCode(null);
            }
            setUnauthorized(false);
            setGuilds([]);
          });
        }
      }
    };
    void run();
    return () => {
      mounted = false;
      detachGuildsStream(token, handlers);
    };
  }, [token, attempt]);
  const sorted = dedupeGuilds(guilds ?? []).slice().sort((a, b) => Number(!isLockedGuild(b)) - Number(!isLockedGuild(a)));
  const loading = guilds === null && !error && !unauthorized;
  const authRequired = (error ?? "").includes("discord_auth_required");
  async function handleRefresh(): Promise<void> {
    if (refreshing) return;
    setRefreshing(true);
    setError(null);
    try {
      const fresh = await fetchGuilds(token, undefined, undefined, true);
      setGuilds(dedupeGuilds(fresh));
      setError(null);
      setErrorCode(null);
      setUnauthorized(false);
      const seen = new Set<string>();
      await fetchGuildsStream(
        token,
        {
          onGuilds: (list) => setGuilds(dedupeGuilds(list)),
          onPresence: (g) => {
            if (!g || typeof g.id !== "string" || g.id === "") return;
            if (seen.has(g.id + ":" + String(g.botPresent) + ":" + String(g.embeddable))) return;
            seen.add(g.id + ":" + String(g.botPresent) + ":" + String(g.embeddable));
            setGuilds((prev) => {
              if (!prev) return [g];
              const idx = prev.findIndex((x) => x.id === g.id);
              if (idx === -1) return [...prev, g];
              const next = [...prev];
              const cur = next[idx];
              if (!cur) return prev;
              next[idx] = { ...cur, ...g };
              return next;
            });
          },
          onDone: () => {},
        },
        undefined,
        true
      );
    } catch (e) {
      if (e instanceof DOMException && e.name === "AbortError") return;
      if (isApiError(e) && e.status === 401) {
        setUnauthorized(true);
        setGuilds([]);
        return;
      }
      setError(e instanceof Error ? e.message : "Could not load bakeries.");
      setErrorCode(isApiError(e) ? e.status : null);
    } finally {
      setRefreshing(false);
    }
  }
  function handleJoin(g: BakeryGuild): void {
    const clean = normalizeGuildId(g.id);
    if (clean === null) {
      setJoinErrors((prev) => ({ ...prev, [g.id]: "Invalid bakery id — pick another server." }));
      return;
    }
    setJoinErrors((prev) => {
      if (!(g.id in prev)) return prev;
      const next: Record<string, string> = { ...prev };
      delete next[g.id];
      return next;
    });
    onJoin(clean);
  }
  return (
    <div className="cb-page">
      <div className="cb-panel hl center">
        <h1 className="cb-h">🍪 Pick your bakery{user ? `, ${user.username}` : ""}</h1>
        <p className="small dim">Choose a server bakery to join.</p>
        <div style={{ marginTop: 8 }}>
          <button type="button" className={refreshing ? "cb-buy off" : "cb-buy"} disabled={refreshing} onClick={() => void handleRefresh()}>
            {refreshing ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      </div>
      {unauthorized && (
        <div className="cb-panel center small" role="alert">
          Session expired. Please sign in again.{" "}
          <a className="cb-buy" href={loginUrl()} style={{ textDecoration: "none", display: "inline-block", marginLeft: 8 }}>
            Continue with Discord
          </a>
        </div>
      )}
      {error && !unauthorized && (
        <div className="cb-panel center small" role="alert">
          {errorCode !== null ? friendlyLoadError(error) + " (code " + errorCode + ")" : friendlyLoadError(error)}{" "}
          <button type="button" className="cb-buy" style={{ marginLeft: 8 }} onClick={() => setAttempt((a) => a + 1)}>
            Retry
          </button>
          {authRequired && (
            <a className="cb-buy ghost" href={loginUrl()} style={{ textDecoration: "none", display: "inline-block", marginLeft: 8 }}>
              Back to login
            </a>
          )}
        </div>
      )}
      {loading && (
        <div className="cb-panel center small" role="status" aria-live="polite">
          Loading bakeries…
        </div>
      )}
      {guilds && guilds.length === 0 && !error && !unauthorized && (
        <div className="cb-panel center small" role="status">
          No bakeries yet. Install the Cookie Bakery app in your Discord server or create a server, then sign in again.
        </div>
      )}
      <div className="cb-cards">
        {sorted.map((g) => {
          const locked = isLockedGuild(g);
          const reason = guildReason(g);
          const iconUrl = guildIconUrl(g);
          const installUrl = typeof g.installUrl === "string" && g.installUrl.length > 0 ? g.installUrl : null;
          const canInstall = installUrl !== null && locked;
          const joinError = joinErrors[g.id] ?? null;
          const checking = isCheckingGuild(g);
          const pending = checking;
          return (
            <div className={locked ? "cb-card muted" : "cb-card"} key={g.id} style={locked ? { opacity: 0.85 } : undefined}>
              <span className="cb-card-ico" aria-hidden="true" style={locked ? { filter: "grayscale(1)" } : undefined}>
                <GuildIcon url={iconUrl} name={g.name} locked={locked} />
              </span>
              <span className="cb-cname" style={{ flex: 1 }}>
                <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  {g.name}
                  {pending ? (
                    <span
                      aria-label="Checking"
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: 999,
                        background: "#f5b942",
                        display: "inline-block",
                        animation: "cbPulse 1s infinite alternate",
                      }}
                    />
                  ) : locked ? (
                    <span className="cb-lvl" aria-label="Not installed">🔒</span>
                  ) : null}
                </span>
                <small>{guildSubline(g)}</small>
                {joinError !== null && <small role="alert">{joinError}</small>}
                {locked && reason !== "" && <small className="dim">{reason}</small>}
                {locked && reason === "" && <small className="dim">Bot not installed</small>}
              </span>
              <span style={{ display: "flex", gap: 8, alignItems: "center" }}>
                {checking ? (
                  <>
                    {installUrl !== null && (
                      <button type="button" className="cb-buy off" disabled={true}>
                        Install
                      </button>
                    )}
                    {!locked && (
                      <button type="button" className="cb-buy off" disabled={true}>
                        Join Bakery
                      </button>
                    )}
                  </>
                ) : (
                  <>
                    {canInstall && (
                      <a className="cb-buy ghost" href={installUrl as string} target="_blank" rel="noreferrer" style={{ textDecoration: "none" }}>
                        Install
                      </a>
                    )}
                    {!locked && (
                      <button type="button" className="cb-buy" onClick={() => handleJoin(g)}>
                        Join Bakery
                      </button>
                    )}
                  </>
                )}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
